import { DS_INPUT, Field as DsField } from "@/components/ds/field";
import { btn } from "@/components/ds/styles";
import { createFileRoute, useNavigate, useSearch, Link } from "@tanstack/react-router";
import { PublicShell } from "@/components/public/public-shell";
import { useEffect, useId, useState } from "react";
import { z } from "zod";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { resolveLandingPath } from "@/lib/auth/landing.functions";
import { InAppBrowserBanner } from "@/components/common/in-app-browser-banner";
import { detectInAppBrowser } from "@/lib/in-app-browser";
import logoWide from "@/assets/drsara-logo-maroon.png.asset.json";
import { oauthReturnPath } from "@/lib/oauth-return-path";

const searchSchema = z.object({
  mode: z.enum(["sign_in", "sign_up"]).optional(),
  redirect: z.string().optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: searchSchema,
  head: () => ({
    meta: [
      { title: "تسجيل الدخول — د. سارة النحّاس" },
      { name: "description", content: "الدخول إلى الحساب على منصّة د. سارة النحّاس." },
      { name: "robots", content: "noindex, nofollow, noarchive, nosnippet" },
    ],
  }),
  component: AuthPageInShell,
});

function AuthPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth" });
  const [mode, setMode] = useState<"sign_in" | "sign_up">(search.mode ?? "sign_in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [awaitingConfirm, setAwaitingConfirm] = useState<string | null>(null);
  const [showSignUpHint, setShowSignUpHint] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const [inAppApp, setInAppApp] = useState<string | null | undefined>(undefined);
  const [isAnon, setIsAnon] = useState(false);
  const [modeDefaulted, setModeDefaulted] = useState(Boolean(search.mode));

  useEffect(() => {
    const detected = detectInAppBrowser();
    if (detected.isInApp) setInAppApp(detected.app);
  }, []);

  // Mobile keyboards capitalise and autocorrect; normalise only at submit.
  const cleanEmail = () => email.trim().toLowerCase();

  async function landingFor(explicit?: string): Promise<string> {
    if (explicit) {
      const safe = oauthReturnPath(explicit);
      if (safe !== "/") return safe;
    }
    try {
      return await resolveLandingPath();
    } catch {
      return "/bousala";
    }
  }

  useEffect(() => {
    // Trust boundary: use getUser() to re-validate identity before redirecting
    // an already-signed-in visitor. Anonymous users must stay here so they can
    // convert their session or sign in to an existing account.
    supabase.auth.getUser().then(async ({ data }) => {
      const user = data.user;
      setIsAnon(user?.is_anonymous === true);
      if (!user || user.is_anonymous) return;
      const dest = await landingFor(search.redirect);
      navigate({ to: dest as any, replace: true });
    });
  }, [navigate, search.redirect]);

  useEffect(() => {
    // Anonymous visitors arriving without an explicit mode are almost always
    // trying to keep a result they just produced — default to sign up.
    if (isAnon && !search.mode && !modeDefaulted) {
      setMode("sign_up");
      setModeDefaulted(true);
    }
  }, [isAnon, search.mode, modeDefaulted]);

  async function handleEmail(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (mode === "sign_up" && !isPasswordValid(password)) {
      toast.error("كلمة المرور يجب أن تكون 8 أحرف أو أكثر وتحتوي على حروف وأرقام.");
      return;
    }
    setSubmitting(true);
    try {
      if (mode === "sign_up") {
        // An anonymous visitor already owns a profile, attempt and report.
        // Converting in place keeps the same user_id so nothing is lost.
        const { data: current } = await supabase.auth.getUser();
        const isAnon = current.user?.is_anonymous === true;

        setEmailTaken(false);
        if (isAnon) {
          const { data, error } = await supabase.auth.updateUser({
            email: cleanEmail(),
            password,
            data: { full_name: fullName.trim() },
          });
          if (error) {
            if (isEmailTaken(error)) {
              setEmailTaken(true);
              return;
            }
            throw error;
          }

          // handle_new_user only runs on INSERT, so the profile name must be
          // written explicitly after a conversion.
          if (data.user?.id && fullName.trim()) {
            await supabase
              .from("profiles")
              .update({ full_name: fullName.trim() })
              .eq("user_id", data.user.id);
          }

          // Email change may need confirmation; the session stays anonymous
          // until then. That is not a failure.
          const { data: after } = await supabase.auth.getUser();
          if (after.user?.is_anonymous === true) {
            setAwaitingConfirm(cleanEmail());
            toast.success("أرسلنا رابط التأكيد إلى بريدك — نتيجتك محفوظة");
            return;
          }
          toast.success("صار عندك حساب، ونتيجتك محفوظة معه");
        } else {
          const { data, error } = await supabase.auth.signUp({
            email: cleanEmail(),
            password,
            options: {
              emailRedirectTo: window.location.origin,
              data: { full_name: fullName.trim() },
            },
          });
          if (error) throw error;
          // With email confirmation enabled, signUp returns no session — the
          // user is NOT signed in yet. Navigating to a gated route here is what
          // produced the "Email not confirmed" login loop.
          if (!data.session) {
            setAwaitingConfirm(cleanEmail());
            toast.success("أرسلنا رابط التأكيد إلى بريدك");
            return;
          }
          toast.success("صار عندك حساب. أهلاً فيك");
        }
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email: cleanEmail(),
          password,
        });
        if (error) throw error;
        setShowSignUpHint(false);
        toast.success("أهلاً فيك من جديد");
      }
      const dest = await landingFor(search.redirect);
      navigate({ to: dest as any, replace: true });
    } catch (err) {
      // Always offered on any invalid-credentials failure — never conditioned on
      // whether the email exists (that would leak account existence).
      setShowSignUpHint(mode === "sign_in" && isInvalidCredentials(err));
      toast.error(friendly(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResendConfirmation() {
    if (submitting || !awaitingConfirm) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: awaitingConfirm.trim().toLowerCase(),
        options: { emailRedirectTo: window.location.origin },
      });
      if (error) throw error;
      toast.success("أرسلنا الرابط مرة أخرى.");
    } catch (err) {
      toast.error(friendly(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgot() {
    if (resetting) return;
    if (!email.trim()) {
      toast.error("البريد مطلوب أولاً");
      return;
    }
    setResetting(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      toast.success("أرسلنا رابط الاستعادة إلى بريدك.");
    } catch (err) {
      toast.error(friendly(err));
    } finally {
      setResetting(false);
    }
  }

  async function handleGoogle() {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Persist the intended destination across the OAuth round-trip.
      // The provider redirects back to a public route (`/auth`); the
      // useEffect above then reads `?redirect=` and navigates.
      const redirectQs = search.redirect ? `?redirect=${encodeURIComponent(search.redirect)}` : "";
      const result = await lovable.auth.signInWithOAuth("google", {
        redirect_uri: `${window.location.origin}/auth${redirectQs}`,
      });
      if (result.error) throw result.error;
      if (result.redirected) return;
      const dest = await landingFor(search.redirect);
      navigate({ to: dest as any, replace: true });
    } catch (err) {
      toast.error(friendly(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-cream-200">
      <div className="mx-auto flex min-h-[70svh] max-w-6xl flex-col-reverse items-stretch justify-center gap-8 px-6 py-10 md:flex-row md:items-center">
        {/* Form card */}
        <div className="mx-auto w-full max-w-md rounded-ds-xl bg-surface p-8 shadow-sm">
          <img src={logoWide.url} alt="د. سارة النحّاس" className="mb-6 h-12 w-auto" />
          <InAppBrowserBanner />
          {awaitingConfirm ? (
            <div>
              <h1 className="font-display text-2xl font-extrabold text-foreground">
                رابط التأكيد بالبريد
              </h1>
              <p className="mt-3 leading-loose text-brown-500">
                أرسلنا رابط تأكيد إلى{" "}
                <span className="font-bold text-foreground">{awaitingConfirm}</span>. بعد فتح الرابط، تسجيل الدخول من هون. إذا ما وصل، ممكن يكون بمجلّد الرسائل غير المرغوب فيها.
              </p>
              <button
                type="button"
                onClick={handleResendConfirmation}
                disabled={submitting}
                className={`${btn.primary} mt-6 w-full disabled:opacity-50`}
              >
                {submitting ? "جارٍ الإرسال..." : "أعِد إرسال رابط التأكيد"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setAwaitingConfirm(null);
                  setMode("sign_in");
                }}
                className="mt-3 w-full rounded-full border border-hairline-default bg-surface px-5 py-3 font-display text-sm font-bold text-foreground transition-colors hover:bg-cream-100"
              >
                العودة إلى تسجيل الدخول
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-2xl font-extrabold text-foreground">
                {mode === "sign_in" ? "أهلاً فيك من جديد" : "أنشئ حسابك"}
              </h1>
              <p className="mt-2 text-sm text-brown-500">
                {mode === "sign_in"
                  ? "تسجيل الدخول لمتابعة رحلتك."
                  : "بضع خطوات ونبدأ رحلتك معنا."}
              </p>

              {isAnon && (
                <div className="mt-4 rounded-ds-lg bg-accent/15 p-4 text-center">
                  <p className="text-sm leading-relaxed text-foreground">
                    إذا أنشأت حساباً الآن، نتيجتك الحالية رح تنحفظ معه وما تضيع.
                  </p>
                </div>
              )}

              {isAnon && mode === "sign_in" && (
                <div className="mt-4 rounded-ds-lg border border-amber-300 bg-amber-100 p-4 text-center">
                  <p className="text-sm leading-relaxed text-foreground">
                    إذا سجّلت الدخول بحساب ثاني، نتيجتك الحالية ما رح تنتقل له.
                  </p>
                  <p className="mt-1 text-[13px] leading-relaxed text-brown-500">
                    بس تقدر ترجع لها أي وقت من رابط الحفظ الموجود بصفحة النتيجة.
                  </p>
                </div>
              )}

              <button
                type="button"
                onClick={handleGoogle}
                disabled={submitting}
                className="mt-6 flex min-h-12 w-full items-center justify-center gap-3 rounded-ds-pill border border-hairline-default bg-surface px-5 font-display text-[16px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-50 disabled:opacity-50"
              >
                <GoogleIcon />
                المتابعة عبر Google
              </button>

              {inAppApp !== undefined && (
                <p className="mt-2 text-center text-[13px] leading-relaxed text-brown-500">
                  أحياناً لا يعمل الدخول عبر Google داخل متصفح {inAppApp ?? "التطبيق"} — الحل فتح الصفحة
                  بالمتصفح، أو الدخول بالبريد وكلمة المرور بالأسفل.
                </p>
              )}

              <div className="my-6 flex items-center gap-3 text-[13px] text-brown-500">
                <span className="h-px flex-1 bg-border" />
                أو
                <span className="h-px flex-1 bg-border" />
              </div>

              <form onSubmit={handleEmail} className="space-y-4">
                {mode === "sign_up" && (
                  <Field
                    label="الاسم الكامل"
                    value={fullName}
                    onChange={setFullName}
                    type="text"
                    autoComplete="name"
                    required
                  />
                )}
                <Field
                  label="البريد الإلكتروني"
                  value={email}
                  onChange={setEmail}
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
                <Field
                  label="كلمة المرور"
                  value={password}
                  onChange={setPassword}
                  type="password"
                  autoComplete={mode === "sign_in" ? "current-password" : "new-password"}
                  minLength={8}
                  required
                />
                {mode === "sign_up" && (
                  <p className="-mt-2 text-[13px] leading-relaxed text-brown-500">
                    يجب أن تكون 8 أحرف أو أكثر، وتحتوي على حروف وأرقام معاً.
                  </p>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className={`${btn.primary} mt-2 w-full disabled:opacity-50`}
                >
                  {submitting ? "لحظة..." : mode === "sign_in" ? "تسجيل الدخول" : "إنشاء الحساب"}
                </button>
              </form>

              {mode === "sign_in" && (
                <div className="mt-3 text-center">
                  <button
                    type="button"
                    onClick={handleForgot}
                    disabled={resetting}
                    className="inline-flex min-h-12 items-center px-2 text-[14px] font-semibold text-amber-800 hover:underline disabled:opacity-50"
                  >
                    {resetting ? "جارٍ الإرسال..." : "نسيت كلمة المرور؟"}
                  </button>
                </div>
              )}

              <p className="mt-6 text-center text-sm text-brown-500">
                {mode === "sign_in" ? "ما عندك حساب؟" : "عندك حساب؟"}{" "}
                <button
                  type="button"
                  onClick={() => setMode(mode === "sign_in" ? "sign_up" : "sign_in")}
                  className="inline-flex min-h-11 items-center font-bold text-amber-800 hover:underline"
                >
                  {mode === "sign_in" ? "إنشاء حساب جديد" : "تسجيل الدخول"}
                </button>
              </p>

              {showSignUpHint && mode === "sign_in" && (
                <div className="mt-5 rounded-ds-lg bg-accent/15 p-4 text-center">
                  <p className="text-sm text-foreground">ما عندك حساب؟</p>
                  <button
                    type="button"
                    onClick={() => {
                      setShowSignUpHint(false);
                      setMode("sign_up");
                    }}
                    className={`${btn.primary} mt-3 w-full`}
                  >
                    أنشئ حساب جديد
                  </button>
                  <p className="mt-2 text-[13px] text-brown-500">
                    سنحتفظ ببريدك وكلمة المرور اللذين كتبتهما.
                  </p>
                </div>
              )}

              {emailTaken && (
                <div className="mt-5 rounded-ds-lg bg-accent/15 p-4 text-center">
                  <p className="text-sm leading-relaxed text-foreground">
                    هذا البريد عنده حساب من قبل. تقدر تسجّل الدخول فيه.
                  </p>
                  <p className="mt-2 text-[13px] leading-relaxed text-brown-500">
                    ونتيجتك الحالية ما ضاعت — تقدر ترجع لها في أي وقت من رابط الحفظ الموجود بصفحة
                    النتيجة.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setEmailTaken(false);
                      setMode("sign_in");
                    }}
                    className={`${btn.primary} mt-3 w-full`}
                  >
                    تسجيل الدخول بهذا البريد
                  </button>
                </div>
              )}

              <p className="mt-4 text-center text-[13px] leading-relaxed text-brown-500">
                المتابعة تعني الموافقة على{" "}
                <Link to="/privacy" className="inline-flex min-h-11 items-center underline">
                  سياسة الخصوصية
                </Link>{" "}
                والشروط.
              </p>
            </>
          )}
        </div>

        {/* Warm right rail */}
        <div className="mx-auto max-w-md text-center md:text-start">
          <p className="kicker text-amber-800">منصّة د. سارة النحّاس</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight text-foreground md:text-4xl">
            رحلتُك من حيرة التربية إلى متعة العلاقة
          </h2>
          <p className="mt-4 leading-loose text-brown-500">
            من داخل حسابك فيك حجز الاستشارات، متابعة الجلسات، والوصول إلى الاختبارات والدورات —
            في مكان واحد بسيط ومطمئن.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  minLength?: number;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  autoCapitalize?: string;
  autoCorrect?: string;
  spellCheck?: boolean;
}) {
  const { label, value, onChange, type = "text", ...rest } = props;
  const id = useId();
  return (
    <DsField id={id} label={label}>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={DS_INPUT}
        {...rest}
      />
    </DsField>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"
        fill="#EA4335"
      />
    </svg>
  );
}

function isEmailTaken(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /already (registered|been registered|exists)|email address is already|already in use|user_already_exists|email_exists/i.test(
    msg,
  );
}

function isInvalidCredentials(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /invalid.*credentials/i.test(msg);
}

function isNetworkIssue(err: unknown): boolean {
  const name = err instanceof Error ? err.name : "";
  const msg = err instanceof Error ? err.message : String(err);
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  if (/AuthRetryableFetchError|NetworkError|TypeError/i.test(name)) return true;
  return /failed to fetch|fetch failed|networkerror|timeout|timed out|offline|err_internet/i.test(
    msg,
  );
}

function isPasswordValid(password: string): boolean {
  return password.length >= 8 && /[a-zA-Z]/.test(password) && /\d/.test(password);
}

function friendly(err: unknown): string {
  const msg = err instanceof Error ? err.message : String(err);
  if (isNetworkIssue(err))
    return "يبدو أن الاتصال بالإنترنت متقطّع. بعد التأكد من الشبكة، المحاولة من جديد ممكنة.";
  if (/invalid.*credentials/i.test(msg)) return "البريد أو كلمة المرور غير صحيحة.";
  if (/not confirmed/i.test(msg))
    return "البريد لسا ما تأكّد. رابط التأكيد بالبريد، وبعده تسجيل الدخول.";
  if (/rate limit|after \d+ seconds/i.test(msg))
    return "أرسلنا رسالة قبل قليل. المحاولة من جديد ممكنة بعد دقيقة.";
  if (/already registered/i.test(msg)) return "هذا الحساب موجود مسبقاً. الدخول يكون من «تسجيل الدخول».";
  if (/pwned|breach|weak/i.test(msg)) return "هذه الكلمة مسرّبة، والأفضل كلمة أقوى.";
  if (/Password should contain at least one character of each/i.test(msg))
    return "كلمة المرور يجب أن تكون 8 أحرف أو أكثر وتحتوي على حروف وأرقام معاً.";
  return "صار خطأ بسيط.";
}

function AuthPageInShell() {
  return (
    <PublicShell ground="bg-cream-200">
      <AuthPage />
    </PublicShell>
  );
}
